// Centralized services data for consistency across all components
export const servicesData = {
  // Core Services (Featured on homepage)
  coreServices: [
    {
      id: 'generative-ai',
      icon: 'bi-cpu',
      title: 'Generative AI Solutions',
      subtitle: 'Production-Ready AI Applications',
      description: 'Build intelligent chatbots, content generators, and AI assistants that deliver immediate business value.',
      features: [
        'Custom LLM Integration',
        'RAG-Powered Knowledge Bases',
        'Multi-Modal AI Applications',
        'Enterprise Security & Compliance'
      ],
      timeline: '8-12 weeks',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
      link: '/services/generative-ai',
      caseStudy: {
        title: 'Seez',
        result: '15% sales increase'
      },
      category: 'ai-ml',
      priority: 1
    },
    {
      id: 'machine-learning',
      icon: 'bi-graph-up',
      title: 'Machine Learning & Analytics',
      subtitle: 'Data-Driven Decision Making',
      description: 'Transform your data into actionable insights with predictive models and automated analytics.',
      features: [
        'Predictive Analytics',
        'Custom ML Models',
        'Real-time Data Processing',
        'Automated Reporting'
      ],
      timeline: '10-14 weeks',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
      link: '/services/machine-learning',
      caseStudy: {
        title: 'Upwork Client Project',
        result: '95% detection accuracy'
      },
      category: 'ai-ml',
      priority: 2
    },
    {
      id: 'chatbot-development',
      icon: 'bi-robot',
      title: 'Intelligent Chatbots',
      subtitle: '24/7 Customer Engagement',
      description: 'Deploy AI-powered chatbots that handle customer queries, automate support, and boost satisfaction.',
      features: [
        'Natural Language Processing',
        'Multi-Channel Integration',
        'Sentiment Analysis',
        'Seamless Human Handoff'
      ],
      timeline: '6-10 weeks',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
      link: '/services/chatbot',
      caseStudy: {
        title: 'Axle Health',
        result: '80% automated requests'
      },
      category: 'ai-ml',
      priority: 3
    }
  ],

    // All Services (Comprehensive list)
  allServices: [
    {
      id: 'generative-ai',
      icon: 'bi-cpu',
      title: 'Generative AI Solutions',
      subtitle: 'Enterprise-Ready AI That Drives Business Growth',
      description: 'Deploy production-grade generative AI systems that transform how your business creates content, automates processes, and engages customers. Our enterprise-focused approach ensures scalability, security, and measurable ROI from day one.',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
      challenges: [
        'Scaling content creation across multiple channels and languages',
        'Maintaining brand consistency while personalizing customer experiences',
        'Integrating AI with legacy enterprise systems and workflows',
        'Ensuring AI-generated content meets compliance and quality standards',
        'Managing costs while scaling AI operations across the organization',
        'Building trust and adoption of AI solutions among stakeholders'
      ],
      playbook: [
        { step: 'Strategic Assessment', description: 'Comprehensive analysis of business objectives, data assets, and AI readiness' },
        { step: 'Solution Architecture', description: 'Design enterprise-grade AI architecture with security and scalability' },
        { step: 'Model Development', description: 'Build, fine-tune, and validate custom generative AI models' },
        { step: 'Integration & Deployment', description: 'Seamless integration with existing systems and production deployment' },
        { step: 'Monitoring & Optimization', description: 'Continuous monitoring, performance optimization, and model updates' }
      ],
      benefits: [
        '75% reduction in content creation costs with enterprise-grade automation',
        '10x faster time-to-market for AI-powered applications',
        '99.9% uptime with enterprise security and compliance',
        '40% increase in customer engagement through personalized AI experiences',
        'Seamless integration with 200+ enterprise systems and APIs',
        'Dedicated AI success team with 24/7 monitoring and optimization'
      ],
      expertise: [
        'Large Language Model Integration & Fine-tuning',
        'Retrieval-Augmented Generation (RAG) Systems',
        'Multimodal AI Content Generation',
        'AI Agent Development & Orchestration',
        'Prompt Engineering & Optimization',
        'Vector Database Design & Management',
        'AI Model Deployment & Scaling',
        'Content Moderation & Safety Systems',
        'Enterprise AI Integration & APIs'
      ],
      technologies: [
        'OpenAI GPT-4 & GPT-4 Turbo',
        'Anthropic Claude 3.5 Sonnet',
        'Google Gemini Pro & Ultra',
        'LangChain & LlamaIndex Frameworks',
        'Pinecone & Weaviate Vector DBs',
        'Stable Diffusion & Midjourney APIs',
        'AutoGen & CrewAI Agent Frameworks',
        'N8N & Zapier Workflow Automation',
        'Hugging Face Transformers Hub'
      ],
      category: 'ai-ml',
      priority: 1
    },
    {
      id: 'machine-learning',
      icon: 'bi-graph-up',
      title: 'Machine Learning & Deep Learning',
      subtitle: 'Intelligent Data-Driven Solutions',
      description: 'Unlock the power of your data with advanced machine learning solutions. We build intelligent systems that learn, adapt, and deliver actionable insights for your business growth.',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
      challenges: [
        'Complex data processing and analysis requirements',
        'Need for predictive modeling and pattern recognition',
        'Scalable model training and deployment',
        'Real-time decision making capabilities',
        'Model accuracy and performance optimization'
      ],
      playbook: [
        { step: 'Data Analysis', description: 'Analyze and prepare data for machine learning' },
        { step: 'Model Development', description: 'Design and train ML/DL models' },
        { step: 'Validation', description: 'Test and validate model performance' },
        { step: 'Deployment', description: 'Deploy models to production environment' }
      ],
      benefits: [
        '95%+ prediction accuracy with advanced algorithms',
        'Real-time decision making and insights',
        'Automated processes that scale with your business',
        'Continuous learning and model improvement',
        'Reduced operational costs by 40-60%',
        'Expert support and ongoing optimization'
      ],
      expertise: [
        'Deep Learning & Neural Network Architecture',
        'Computer Vision & Image Recognition',
        'Predictive Analytics & Forecasting',
        'Time Series Analysis & Modeling',
        'MLOps & Model Lifecycle Management',
        'Experiment Tracking & Model Monitoring',
        'Cloud ML Platforms & Infrastructure',
        'AutoML & Hyperparameter Optimization',
        'Model Performance Optimization & Tuning'
      ],
      technologies: [
        'TensorFlow 2.x & Keras',
        'PyTorch & Lightning Framework',
        'Scikit-learn & XGBoost',
        'Apache Spark & MLlib',
        'MLflow & Kubeflow Pipelines',
        'Weights & Biases (W&B)',
        'AWS SageMaker & Sagemaker Studio',
        'Google Vertex AI & AutoML',
        'Azure Machine Learning'
      ],
      category: 'ai-ml',
      priority: 2
    },
    {
      id: 'nlp',
      icon: 'bi-chat-text',
      title: 'Natural Language Processing (NLP)',
      subtitle: 'Intelligent Text Processing',
      description: 'Harness the power of language with advanced NLP solutions. From sentiment analysis to intelligent text processing, we build systems that understand and process human language at scale.',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
      challenges: [
        'Processing large volumes of unstructured text data',
        'Understanding context and sentiment in text',
        'Building conversational AI systems',
        'Multilingual text processing',
        'Real-time text analysis and classification'
      ],
      playbook: [
        { step: 'Text Analysis', description: 'Analyze text data and identify processing needs' },
        { step: 'Model Development', description: 'Build NLP models for specific tasks' },
        { step: 'Training', description: 'Train models on relevant text datasets' },
        { step: 'Integration', description: 'Integrate NLP capabilities into applications' }
      ],
      benefits: [
        'Automated text analysis and processing',
        'Improved customer sentiment understanding',
        'Intelligent document processing',
        'Multilingual support capabilities',
        'Enhanced search and recommendation systems',
        'Continuous support and model updates'
      ],
      expertise: [
        'Transformer Models & BERT/GPT Integration',
        'Sentiment Analysis & Emotion Detection',
        'Named Entity Recognition & Information Extraction',
        'Machine Translation & Multilingual NLP',
        'Question Answering & Conversational AI',
        'Text Summarization & Content Generation',
        'Speech-to-Text & Audio Processing',
        'Semantic Search & Vector Similarity',
        'NLP Model Fine-tuning & Transfer Learning'
      ],
      technologies: [
        'BERT & RoBERTa Models',
        'GPT-3.5 & GPT-4 Transformers',
        'SpaCy & NLTK Libraries',
        'Hugging Face Transformers Hub',
        'Sentence Transformers',
        'OpenAI Whisper (Speech-to-Text)',
        'Cohere & Anthropic Claude APIs',
        'Google PaLM & BERT Models',
        'FastAI & ULMFiT Frameworks'
      ],
      category: 'ai-ml',
      priority: 3
    },
    {
      id: 'chatbot',
      icon: 'bi-robot',
      title: 'Chatbot Development',
      subtitle: 'Intelligent Customer Engagement',
      description: 'Revolutionize customer engagement with intelligent AI chatbots. We create conversational AI systems that provide 24/7 support, automate interactions, and deliver exceptional user experiences.',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
      challenges: [
        'Providing 24/7 customer support',
        'Handling complex customer queries',
        'Integrating with existing business systems',
        'Ensuring natural conversation flow',
        'Scaling support operations efficiently'
      ],
      playbook: [
        { step: 'Requirements Analysis', description: 'Define chatbot requirements and use cases' },
        { step: 'Design', description: 'Design conversation flows and user experience' },
        { step: 'Development', description: 'Build and train the chatbot system' },
        { step: 'Testing', description: 'Test and optimize chatbot performance' }
      ],
      benefits: [
        '24/7 customer support availability',
        'Reduced support costs by 30-50%',
        'Improved customer satisfaction',
        'Faster response times',
        'Scalable support operations',
        'Ongoing maintenance and improvements'
      ],
      expertise: [
        'Conversational AI & Dialog Management',
        'Intent Recognition & Entity Extraction',
        'Multi-turn Dialogues & Context Management',
        'Voice Integration & Speech Recognition',
        'Omnichannel Support & Platform Integration',
        'Workflow Automation & API Integration',
        'Natural Language Understanding (NLU)',
        'Chatbot Analytics & Performance Optimization',
        'Customer Support Automation'
      ],
      technologies: [
        'Dialogflow & Google Cloud AI',
        'Microsoft Bot Framework & Azure',
        'Rasa Open Source & Rasa X',
        'IBM Watson Assistant',
        'Amazon Lex & Connect',
        'LangChain & LlamaIndex Frameworks',
        'N8N & Zapier Workflow Automation',
        'Twilio & Vonage Communication APIs',
        'Slack & Discord Bot APIs'
      ],
      category: 'ai-ml',
      priority: 4
    },

    {
      id: 'saas',
      icon: 'bi-cloud',
      title: 'SaaS Product Development',
      subtitle: 'Build, launch, and scale your SaaS securely',
      description: 'We design, build, and scale secure, multi‑tenant SaaS with delightful UX, billing, analytics, and DevOps. Full development lifecycle and ongoing support included.',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
      challenges: [
        'Shipping MVPs quickly with quality',
        'Designing great UX that converts',
        'Building secure multi‑tenant architecture',
        'Setting up subscriptions and billing',
        'Scaling with DevOps and monitoring'
      ],
      playbook: [
        { step: 'Discovery', description: 'Scope, personas, pricing, KPIs' },
        { step: 'Prototype', description: 'Clickable flows and technical spike' },
        { step: 'Build', description: 'Multi‑tenant app, integrations, billing' },
        { step: 'Launch', description: 'Monitoring, analytics, experiments' }
      ],
      benefits: [
        'Faster time‑to‑market',
        'Higher conversion and retention',
        'Lower servicing costs via self‑serve',
        'Secure and compliant by default',
        'Observable and scalable platform',
        'Full support and maintenance included'
      ],
      expertise: [
        'Multi‑tenant SaaS Architecture & Scalability',
        'Subscription Management & Billing Integration',
        'Design Systems & Accessibility Compliance',
        'Product Analytics & A/B Testing',
        'DevOps, CI/CD & Observability',
        'Security, SSO & Role-Based Access Control',
        'Cloud-Native Development & Deployment',
        'Microservices Architecture & API Design',
        'Database Design & Performance Optimization'
      ],
      technologies: [
        'React & Next.js Framework',
        'Node.js & Express Backend',
        'PostgreSQL & Redis Databases',
        'Stripe & Paddle Payment Processing',
        'AWS/Azure/GCP Cloud Platforms',
        'Docker & Kubernetes Containerization',
        'Vercel & Netlify Deployment',
        'Supabase & Firebase Backend Services',
        'Auth0 & Clerk Authentication'
      ],
      category: 'development',
      priority: 5
    },
    {
      id: 'predictive-analytics',
      icon: 'bi-graph-up',
      title: 'Predictive Analytics',
      subtitle: 'Future Insights & Forecasting',
      description: 'We use advanced modeling techniques to forecast business trends, predict outcomes, and provide actionable insights for strategic decision making. Continuous monitoring and optimization included.',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
      challenges: [
        'Forecasting future business trends and outcomes',
        'Identifying patterns in historical data',
        'Making data-driven strategic decisions',
        'Risk assessment and mitigation',
        'Optimizing business processes and operations'
      ],
      playbook: [
        { step: 'Data Preparation', description: 'Clean and prepare historical data for analysis' },
        { step: 'Model Development', description: 'Build predictive models using advanced algorithms' },
        { step: 'Validation', description: 'Validate model accuracy and performance' },
        { step: 'Deployment', description: 'Deploy predictive analytics solutions' }
      ],
      benefits: [
        'Improved forecasting accuracy',
        'Better risk assessment and management',
        'Optimized business processes',
        'Data-driven strategic decisions',
        'Increased operational efficiency',
        'Ongoing model optimization and support'
      ],
      expertise: [
        'Time Series Forecasting & Trend Analysis',
        'Regression Analysis & Predictive Modeling',
        'Classification Models & Pattern Recognition',
        'Risk Modeling & Assessment',
        'Demand Forecasting & Supply Chain Analytics',
        'Customer Behavior Prediction & Segmentation',
        'Statistical Analysis & Hypothesis Testing',
        'Data Mining & Feature Engineering',
        'Model Validation & Performance Metrics'
      ],
      technologies: [
        'Python & R Statistical Computing',
        'Prophet & ARIMA Time Series',
        'XGBoost & LightGBM ML Models',
        'Tableau & Power BI Visualization',
        'Apache Spark & Databricks',
        'Snowflake & BigQuery Data Warehouses',
        'Alteryx & KNIME Analytics Platforms',
        'SAS & SPSS Statistical Software',
        'Azure Synapse & AWS Redshift'
      ],
      category: 'analytics',
      priority: 6
    },

    {
      id: 'custom-software',
      icon: 'bi-code-slash',
      title: 'Custom Software and Web Development',
      subtitle: 'Bespoke Digital Solutions',
      description: 'We create bespoke software and web solutions tailored to meet your specific business objectives, streamline operations, and drive digital transformation. Full development and support included.',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
      challenges: [
        'Unique business requirements and workflows',
        'Integration with existing systems',
        'Scalable and maintainable code architecture',
        'User experience and interface design',
        'Security and performance optimization'
      ],
      playbook: [
        { step: 'Requirements Gathering', description: 'Understand business needs and objectives' },
        { step: 'Design', description: 'Design system architecture and user experience' },
        { step: 'Development', description: 'Build custom software solutions' },
        { step: 'Testing', description: 'Test and deploy the solution' }
      ],
      benefits: [
        'Tailored solutions for specific business needs',
        'Improved operational efficiency',
        'Enhanced user experience',
        'Scalable and maintainable systems',
        'Competitive advantage through custom technology',
        'Full support and maintenance included'
      ],
      expertise: [
        'Full-Stack Development & Modern Frameworks',
        'Mobile App Development & Cross-Platform Solutions',
        'API Development & Microservices Architecture',
        'Database Design & Performance Optimization',
        'Cloud Architecture & Multi-Cloud Deployment',
        'DevOps Implementation & CI/CD Pipelines',
        'Frontend Development & User Experience',
        'Backend Development & Server Architecture',
        'Security Implementation & Best Practices'
      ],
      technologies: [
        'React & Next.js Frontend',
        'Node.js & Express Backend',
        'Python & Django/FastAPI',
        'Java & Spring Boot Framework',
        'AWS & Azure & GCP Cloud',
        'Docker & Kubernetes Containers',
        'PostgreSQL & MongoDB Databases',
        'Redis & Elasticsearch Search',
        'GraphQL & REST API Development'
      ],
      category: 'development',
      priority: 7
    },
    {
      id: 'ui-ux',
      icon: 'bi-palette',
      title: 'UI/UX Design',
      subtitle: 'User-Centered Design',
      description: 'We provide comprehensive UI/UX design services that enhance user experience, improve engagement, and create intuitive digital interfaces. Full design lifecycle and support included.',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
      challenges: [
        'Creating intuitive and engaging user interfaces',
        'Ensuring accessibility and usability',
        'Designing for multiple devices and platforms',
        'Balancing aesthetics with functionality',
        'User research and testing'
      ],
      playbook: [
        { step: 'Research', description: 'Conduct user research and analyze requirements' },
        { step: 'Design', description: 'Create wireframes and visual designs' },
        { step: 'Prototyping', description: 'Build interactive prototypes for testing' },
        { step: 'Testing', description: 'Test with users and iterate designs' }
      ],
      benefits: [
        'Improved user satisfaction and engagement',
        'Reduced user training and support costs',
        'Increased conversion rates',
        'Better accessibility and usability',
        'Competitive advantage through superior design',
        'Ongoing design support and iterations'
      ],
      expertise: [
        'User Research & User Experience Strategy',
        'Wireframing & Information Architecture',
        'Visual Design & Brand Identity',
        'Prototyping & Interactive Design',
        'Usability Testing & User Feedback Analysis',
        'Design Systems & Component Libraries',
        'Accessibility Design & WCAG Compliance',
        'Mobile-First & Responsive Design',
        'Design Handoff & Developer Collaboration'
      ],
      technologies: [
        'Figma & FigJam Design Tools',
        'Adobe XD & Creative Suite',
        'Sketch & InVision Prototyping',
        'Principle & Framer Animation',
        'Miro & Mural Collaboration',
        'Hotjar & FullStory Analytics',
        'UserTesting & Maze Testing',
        'Design Tokens & Storybook',
        'Accessibility & Prototyping Tools'
      ],
      category: 'design',
      priority: 8
    },
    {
      id: 'staff-augmentation',
      icon: 'bi-people-fill',
      title: 'Staff Augmentation',
      subtitle: 'Elite AI & Tech Talent on Demand',
      description: 'Access our curated pool of pre-vetted AI and technology professionals. Scale your team instantly with senior-level talent who integrate seamlessly into your existing workflows and culture.',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
      
      // Talent-focused challenges
      talentChallenges: [
        'Difficulty finding senior-level AI and ML engineers',
        'Long recruitment cycles (3-6 months) for specialized roles',
        'High cost of full-time hires for project-based work',
        'Risk of cultural misalignment and team friction',
        'Managing remote talent across different time zones'
      ],
      
      // Engagement models instead of playbook
      engagementModels: [
        { 
          model: 'Project-Based', 
          description: 'Dedicated talent for specific projects with defined timelines',
          duration: '3-12 months',
          commitment: 'Full-time equivalent'
        },
        { 
          model: 'Part-Time Integration', 
          description: 'Flexible hours to supplement your existing team',
          duration: 'Ongoing',
          commitment: '20-40 hours/week'
        },
        { 
          model: 'Consulting Support', 
          description: 'Expert guidance and mentorship for your internal team',
          duration: '1-6 months',
          commitment: '10-20 hours/week'
        },
        { 
          model: 'Team Extension', 
          description: 'Complete team augmentation with dedicated project management',
          duration: '6+ months',
          commitment: 'Full team integration'
        }
      ],
      
      // Talent benefits instead of generic benefits
      talentBenefits: [
        'Pre-vetted senior professionals (5+ years experience)',
        'Immediate availability (2-3 weeks onboarding)',
        'Cost savings of 40-60% vs full-time hiring',
        'No recruitment fees or HR overhead',
        'Flexible scaling up or down as needed',
        'Guaranteed cultural fit and team integration'
      ],
      
      // Talent profiles with experience levels
      talentProfiles: [
        {
          role: 'Senior AI/ML Engineers',
          experience: '5-8 years',
          skills: ['Python', 'TensorFlow', 'PyTorch', 'MLOps', 'Deep Learning'],
          availability: 'Immediate',
          rate: 'Competitive'
        },
        {
          role: 'Full-Stack Developers',
          experience: '4-7 years',
          skills: ['React', 'Node.js', 'Python', 'AWS', 'Docker'],
          availability: '2-3 weeks',
          rate: 'Market rate'
        },
        {
          role: 'DevOps & Cloud Engineers',
          experience: '5-10 years',
          skills: ['AWS/Azure/GCP', 'Kubernetes', 'Terraform', 'CI/CD'],
          availability: '1-2 weeks',
          rate: 'Premium'
        },
        {
          role: 'Data Scientists & Analysts',
          experience: '3-6 years',
          skills: ['Python', 'R', 'SQL', 'Statistics', 'Data Visualization'],
          availability: 'Immediate',
          rate: 'Competitive'
        }
      ],
      
      // Process instead of playbook
      process: [
        { step: 'Requirements Analysis', description: 'Understand your project needs, team dynamics, and skill requirements' },
        { step: 'Talent Matching', description: 'Present 3-5 pre-vetted candidates with matching expertise and cultural fit' },
        { step: 'Interview & Selection', description: 'Facilitate technical interviews and team fit assessments' },
        { step: 'Onboarding & Integration', description: 'Seamless integration into your team with proper documentation and handover' },
        { step: 'Ongoing Management', description: 'Continuous support, performance monitoring, and relationship management' }
      ],
      
      // Legacy fields for compatibility
      challenges: [
        'Finding qualified AI and technology professionals',
        'High recruitment costs and long hiring cycles',
        'Managing team scalability during project peaks',
        'Ensuring cultural fit and team integration',
        'Maintaining consistent quality and productivity'
      ],
      playbook: [
        { step: 'Assessment', description: 'Evaluate your team needs and project requirements' },
        { step: 'Matching', description: 'Match you with qualified professionals from our talent pool' },
        { step: 'Integration', description: 'Seamlessly integrate professionals into your existing team' },
        { step: 'Management', description: 'Provide ongoing support and performance monitoring' }
      ],
      benefits: [
        'Access to pre-vetted AI and technology experts',
        'Reduced hiring costs by 40-60%',
        'Flexible scaling based on project demands',
        'Faster time-to-market with immediate availability',
        'Reduced administrative overhead and HR burden',
        'Long-term partnership for ongoing success'
      ],
      expertise: [
        'AI/ML Engineers & Data Scientists',
        'Full-Stack Developers & Software Engineers',
        'DevOps Engineers & Cloud Specialists',
        'UI/UX Designers & Product Designers',
        'Project Managers & Technical Leads',
        'Data Engineers & MLOps Specialists',
        'Frontend & Backend Developers',
        'Mobile App Developers & React Native Specialists',
        'Automation Engineers & N8N/Zapier Experts'
      ],
      technologies: [
        'Python & R Programming',
        'React & Node.js Development',
        'TensorFlow & PyTorch ML',
        'AWS & Azure & GCP Cloud',
        'Docker & Kubernetes DevOps',
        'PostgreSQL & MongoDB Databases',
        'LangChain & MCP AI Frameworks',
        'N8N & Zapier Automation',
        'FastAPI & Django Backend'
      ],
      category: 'staffing',
      priority: 9
    }
  ],

  // Service Categories for filtering and organization
  categories: [
    {
      id: 'consulting',
      name: 'Consulting & Strategy',
      description: 'Strategic consulting and enterprise transformation services',
      icon: 'bi-building',
      color: '#0ea5e9'
    },
    {
      id: 'ai-ml',
      name: 'AI & Machine Learning',
      description: 'Cutting-edge artificial intelligence and machine learning solutions',
      icon: 'bi-cpu',
      color: '#0ea5e9'
    },
    {
      id: 'development',
      name: 'Software Development',
      description: 'Custom software and web development services',
      icon: 'bi-code-slash',
      color: '#0ea5e9'
    },
    {
      id: 'analytics',
      name: 'Data Analytics',
      description: 'Data-driven insights and predictive analytics',
      icon: 'bi-graph-up',
      color: '#5b21b6'
    },
    {
      id: 'design',
      name: 'Design Services',
      description: 'UI/UX design and user experience optimization',
      icon: 'bi-palette',
      color: '#4c1d95'
    },
    {
      id: 'staffing',
      name: 'Staff Augmentation',
      description: 'Elite AI and technology talent on demand',
      icon: 'bi-people-fill',
      color: '#0ea5e9'
    }
  ]
};

// Helper functions
export const getServiceById = (id) => {
  return servicesData.allServices.find(service => service.id === id);
};

export const getServicesByCategory = (categoryId) => {
  return servicesData.allServices.filter(service => service.category === categoryId);
};

export const getCoreServices = () => {
  return servicesData.coreServices;
};

export const getAllServices = () => {
  return servicesData.allServices.sort((a, b) => a.priority - b.priority);
};

export const getCategories = () => {
  return servicesData.categories;
}; 
// Service-specific "Why Projects Fail | How We Add Value" content
export const serviceFailureAnalysis = {
  'generative-ai': {
    commonChallenges: [
      'Lack of clear use case definition and business objectives',
      'Insufficient data quality and preparation for training',
      'Poor integration with existing business workflows',
      'Inadequate testing and validation of AI-generated content',
      'Limited understanding of AI model capabilities and limitations',
      'Insufficient prompt engineering and fine-tuning',
      'Lack of proper monitoring and feedback loops',
      'Inadequate security and compliance measures for AI systems'
    ],
    howWeAddValue: [
      'Comprehensive use case analysis and business impact assessment',
      'Advanced data preprocessing and quality assurance protocols',
      'Seamless integration with existing systems and workflows',
      'Rigorous testing and validation frameworks for AI outputs',
      'Expert prompt engineering and model fine-tuning',
      'Continuous monitoring and performance optimization',
      'Enterprise-grade security and compliance implementation',
      'Ongoing support and model improvement services'
    ]
  },
  'machine-learning': {
    commonChallenges: [
      'Poor data quality and insufficient training data',
      'Lack of domain expertise in machine learning algorithms',
      'Inadequate model validation and testing procedures',
      'Poor feature engineering and data preprocessing',
      'Insufficient computational resources for model training',
      'Lack of proper model deployment and monitoring',
      'Inadequate understanding of business requirements',
      'Poor model interpretability and explainability'
    ],
    howWeAddValue: [
      'Advanced data quality assessment and preprocessing pipelines',
      'Deep expertise in ML algorithms and model selection',
      'Comprehensive validation and testing frameworks',
      'Expert feature engineering and data transformation',
      'Scalable cloud infrastructure for model training',
      'Production-ready deployment and monitoring systems',
      'Thorough business requirement analysis and alignment',
      'Model interpretability and explainability solutions'
    ]
  },
  "chatbot": {
    commonChallenges: [
      "Poor natural language understanding and conversation flow design",
      "Inadequate training data for conversational AI and intent recognition",
      "Lack of proper integration with backend systems and APIs",
      "Insufficient testing of chatbot responses and user interactions",
      "Poor handling of edge cases and fallback scenarios",
      "Lack of proper escalation mechanisms to human agents",
      "Inadequate monitoring of chatbot performance and user satisfaction",
      "Poor customization for specific business domains and use cases"
    ],
    howWeAddValue: [
      "Advanced NLP and conversational AI expertise with context awareness",
      "Comprehensive training data collection and intent recognition systems",
      "Seamless backend system integration with real-time data access",
      "Rigorous testing frameworks for chatbot responses and user flows",
      "Intelligent fallback mechanisms and error handling",
      "Smart escalation systems with smooth human handoff",
      "Advanced analytics and performance monitoring dashboards",
      "Domain-specific customization and business logic integration"
    ]
  },
  'nlp': {
    commonChallenges: [
      'Insufficient understanding of natural language complexity',
      'Poor text preprocessing and tokenization',
      'Inadequate training data for specific domains',
      'Lack of proper model selection and architecture',
      'Insufficient evaluation metrics and testing',
      'Poor handling of multilingual and diverse text',
      'Inadequate scalability and performance optimization',
      'Lack of proper error handling and edge cases'
    ],
    howWeAddValue: [
      'Deep expertise in natural language processing techniques',
      'Advanced text preprocessing and tokenization methods',
      'Domain-specific training data collection and curation',
      'Optimal model selection and architecture design',
      'Comprehensive evaluation metrics and testing frameworks',
      'Multilingual and cross-cultural text processing',
      'Scalable and high-performance NLP solutions',
      'Robust error handling and edge case management'
    ]
  },
  "saas": {
    commonChallenges: [
      "Poor user experience design and intuitive interface development",
      "Inadequate scalability planning and performance optimization",
      "Lack of proper security measures and data protection protocols",
      "Insufficient testing across different browsers and devices",
      "Poor integration with third-party services and APIs",
      "Inadequate monitoring and analytics implementation",
      "Lack of proper deployment and DevOps practices",
      "Insufficient user feedback collection and iteration cycles"
    ],
    howWeAddValue: [
      "User-centric design with intuitive interface and seamless navigation",
      "Scalable cloud architecture with performance optimization",
      "Enterprise-grade security with data encryption and compliance",
      "Comprehensive cross-browser and device testing protocols",
      "Seamless third-party service integration and API management",
      "Advanced monitoring, analytics, and user behavior tracking",
      "Modern DevOps practices with automated deployment pipelines",
      "Continuous user feedback collection and rapid iteration cycles"
    ]
  },
  'predictive-analytics': {
    commonChallenges: [
      'Poor data quality and insufficient historical data',
      'Lack of proper statistical modeling expertise',
      'Inadequate feature selection and engineering',
      'Insufficient model validation and testing',
      'Poor understanding of business context and requirements',
      'Inadequate real-time processing capabilities',
      'Lack of proper model interpretability',
      'Insufficient monitoring and model maintenance'
    ],
    howWeAddValue: [
      'Advanced data quality assessment and historical data analysis',
      'Expert statistical modeling and algorithm selection',
      'Intelligent feature selection and engineering',
      'Comprehensive model validation and testing frameworks',
      'Deep business context understanding and requirement analysis',
      'Real-time processing and streaming analytics capabilities',
      'Model interpretability and explainability solutions',
      'Continuous monitoring and model maintenance services'
    ]
  },
  'custom-software': {
    commonChallenges: [
      'Poor requirement gathering and analysis',
      'Inadequate architecture and system design',
      'Lack of proper development methodologies',
      'Insufficient testing and quality assurance',
      'Poor project management and communication',
      'Inadequate scalability and performance planning',
      'Lack of proper documentation and maintenance',
      'Insufficient user training and support'
    ],
    howWeAddValue: [
      'Comprehensive requirement gathering and analysis',
      'Robust architecture and system design',
      'Agile development methodologies and best practices',
      'Rigorous testing and quality assurance protocols',
      'Effective project management and communication',
      'Scalable and high-performance system design',
      'Comprehensive documentation and maintenance services',
      'Thorough user training and ongoing support'
    ]
  },
  "ui-ux": {
    commonChallenges: [
      "Poor user research and inadequate understanding of target audience",
      "Inconsistent design system and lack of brand guidelines",
      "Lack of proper usability testing and user feedback validation",
      "Insufficient accessibility considerations and WCAG compliance",
      "Poor collaboration between design and development teams",
      "Inadequate responsive design implementation across devices",
      "Lack of proper design documentation and handoff processes",
      "Insufficient user journey mapping and interaction design"
    ],
    howWeAddValue: [
      "Comprehensive user research with persona development and user journey mapping",
      "Consistent design system with comprehensive brand guidelines",
      "Rigorous usability testing with real user feedback validation",
      "Full accessibility compliance with WCAG 2.1 AA standards",
      "Seamless design-development collaboration with clear handoff processes",
      "Responsive design implementation optimized for all devices",
      "Comprehensive design documentation with detailed specifications",
      "Advanced interaction design with smooth user experience flows"
    ]
  },
  'staff-augmentation': {
    commonChallenges: [
      'Difficulty finding senior-level AI and ML engineers',
      'Long recruitment cycles (3-6 months) for specialized roles',
      'High cost of full-time hires for project-based work',
      'Risk of cultural misalignment and team friction',
      'Managing remote talent across different time zones',
      'Limited access to specialized AI talent pool',
      'Inconsistent quality and skill levels from external hires',
      'Complex onboarding and knowledge transfer processes'
    ],
    howWeAddValue: [
      'Pre-vetted senior professionals (5+ years experience)',
      'Immediate availability (2-3 weeks onboarding)',
      'Cost savings of 40-60% vs full-time hiring',
      'No recruitment fees or HR overhead',
      'Flexible scaling up or down as needed',
      'Guaranteed cultural fit and team integration',
      'Dedicated project management and support',
      'Long-term partnership for ongoing success'
    ]
  }
};

// General service failure analysis for main services page
export const generalServiceFailureAnalysis = {
  commonChallenges: [
    'Lack of clear project scope and requirements definition',
    'Insufficient technical expertise and domain knowledge',
    'Poor project management and communication',
    'Inadequate testing and quality assurance processes',
    'Lack of proper planning and resource allocation',
    'Insufficient stakeholder involvement and feedback',
    'Poor technology selection and architecture decisions',
    'Inadequate risk management and contingency planning'
  ],
  howWeAddValue: [
    'Comprehensive project scoping and requirement analysis',
    'Deep technical expertise across multiple domains',
    'Agile project management and transparent communication',
    'Rigorous testing and quality assurance protocols',
    'Detailed planning and optimal resource allocation',
    'Continuous stakeholder engagement and feedback loops',
    'Strategic technology selection and robust architecture',
    'Proactive risk management and mitigation strategies'
  ]
};
